import Component from "./component.js";
import config from "./config.js";
import AnalyticsService from "./libs/snake.lib.analytics.js";
import ApiService from "./libs/snake.lib.api.js";
import Hook from "./libs/snake.lib.hook.js";
import Identity from "./libs/snake.lib.identity.js";
import Trace from "./libs/snake.lib.trace.js";
import Utils from "./libs/snake.lib.utils.js";
import Validate from "./libs/snake.lib.validate.js";
import Summary from "./libs/snake.lib.summary.js";
import Storage from "./libs/snake.lib.storage.js";

export default class ComponentProfile {
    static modalI18nGenericOptions;

    static init = async () => {
        Trace.debug("Component _cmpProfile");

        // FR-2434
        Summary.showReducedVersion(1);

        this.modalI18nGenericOptions = Component.getModalI18nGenericOptions();

        Utils.spinner("loader", true);

        Component.scrollTo(config.routing.sections.profile.component);

        Component.setCheckoutStepsVisibility([config.routing.sections.profile.component], true);

        if (Identity.isAuth()) {
            this.#renderUserProfile();

            await this.#profileFormInit();

            Component.load(config.routing.sections.addresses.component);
        } else this.#signUpAndSignInFormInit();

        Utils.spinner("loader", false);
    };

    static #profileFormInit = async () => {
        Trace.warn("profileFormInit");

        /**
         * Call BigCommerce updateCustomer to add the customer commerce ID to the already created cart
         */
        await this.#updateCustomer(window.snakeCheckout.accountInfo.profile.firstName, window.snakeCheckout.accountInfo.profile.lastName, window.snakeCheckout.accountInfo.profile.email, window.snakeCheckout.accountInfo.gigyaId, window.snakeCheckout?.initData?.cart_id ?? null, Identity.isAgent() || Identity.isAgentCustomer());

        /**
         * Listener to intercept the Profile Edit user action
         */
        Hook.subscribe("_editProfile", () => {
            Utils.spinner("loader", true);

            Component.setCheckoutStepsVisibility([config.routing.sections.addresses.component, config.routing.sections.payment.component], false);

            const componentDom = document.getElementById(config.routing.sections.profile.component);

            const profileForm = componentDom.querySelector(".auth > form");

            profileForm.querySelector('input[name="edit-firstname"]').value = window.snakeCheckout.accountInfo.profile.firstName;
            profileForm.querySelector('input[name="edit-lastname"]').value = window.snakeCheckout.accountInfo.profile.lastName;
            profileForm.querySelector('input[name="edit-email"]').value = window.snakeCheckout.accountInfo.profile.email;

            if (!profileForm.dataset.validationAttached) {
                Validate.attach(profileForm, (formIsValid) => (profileForm.querySelector('button[type="submit"]').disabled = !formIsValid));
            }

            componentDom.querySelector(".auth .profile-info").classList.add("hidden");
            componentDom.querySelector(".auth form").classList.remove("hidden");

            Utils.spinner("loader", false);
        });

        /**
         * Listener to intercept the setAccountInfo call on the Profile Edit user action
         */
        Hook.subscribe("_setProfile", async (e) => {
            Utils.spinner("loader", true);

            const formData = new FormData(e.detail.event.target.closest("form"));

            window.snakeCheckout.accountInfo.profile.firstName = formData.get("edit-firstname");
            window.snakeCheckout.accountInfo.profile.lastName = formData.get("edit-lastname");
            window.snakeCheckout.accountInfo.profile.email = formData.get("edit-email");
            window.snakeCheckout.accountInfo.profile.changed = true;

            Component.load(config.routing.sections.profile.component);
        });
    };

    static #updateCustomer = async (firstname, lastname, email, gigyaId, cartId, isAgent) => {
        Trace.warn("updateCustomer");

        /**
         * On sign-in update the BigCommerce account to add the customer commerce ID to the already created cart.
         * On profile edit update the BigCommerce account with the new data.
         */
        await ApiService.updateCustomer(firstname, lastname, email, gigyaId, cartId, isAgent)
            .then(async (response) => {
                const commerceId = response.data.customer.id;

                Trace.warn("ApiService.updateCustomer");
                Trace.log(response);

                /**
                 * If for some reason the Commerce ID on the Gigya account is missing or different, update the Gigya accout.
                 * On profile edit update the Gigya account with the new data.
                 */
                if (window.snakeCheckout.accountInfo.data.commerceId !== commerceId || window.snakeCheckout.accountInfo.profile.changed) {
                    await Identity.setAccountInfo(firstname, lastname, email, commerceId)
                        .then(() => {
                            Trace.warn("ApiService.updateCustomer Identity.setAccountInfo");

                            window.snakeCheckout.accountInfo.data.commerceId = commerceId;
                            delete window.snakeCheckout.accountInfo.profile.changed;
                        })
                        .catch((e) => {
                            Utils.modal("popup", true, this.modalI18nGenericOptions);

                            AnalyticsService.errorEvent("1_registration", "technical", "snakeAPI, gproxy | create commerce customer & set user account info error", e);
                        });
                }
            })
            .catch((e) => {
                Utils.modal("popup", true, this.modalI18nGenericOptions);

                AnalyticsService.errorEvent("1_registration", "technical", "snakeAPI | create commerce customer error", e);
            });
    };

    static #signUpAndSignInFormInit = () => {
        Trace.warn("signUpAndSignInFormInit");

        Utils.initTabs();

        const componentDom = document.getElementById(config.routing.sections.profile.component);

        const signUpForm = componentDom.querySelector(".sign-up > form");
        Validate.attach(signUpForm, (formIsValid) => (signUpForm.querySelector('button[type="submit"]').disabled = !formIsValid));

        /**
         * Listener to intercept the Sign Up user action
         */
        Hook.subscribe("_signUp", async (e) => {
            Trace.warn("_signUp");

            Utils.spinner("loader", true);

            const form = e.detail.event.target.parentElement.closest("form");
            const formData = new FormData(form);

            await Identity.signUp(formData.get("sign-up-firstname"), formData.get("sign-up-lastname"), formData.get("sign-up-email"))
                .then(async (response) => {
                    Trace.warn("_signUp Identity.signUp");
                    Trace.log(response);

                    if (response.errorCode !== 0) {
                        Utils.modal("popup", true, { text: response.errorMessage, dismissBtn: this.modalI18nGenericOptions.dismissBtn });

                        AnalyticsService.errorEvent("1_registration", "technical", "gproxy | sign-up response error", response);
                    } else {
                        Trace.warn("_signUp PRE LOAD");

                        if (Identity.isAgent()) {
                            Storage.set("agent_order", formData.get("agent-order-type"));

                            setTimeout(() => {
                                window.location.reload();
                            }, 500);
                        } else Storage.delete("agent_order");

                        Component.load(config.routing.sections.load.component);

                        Trace.warn("_signUp POST LOAD");
                    }
                })
                .catch((e) => {
                    /**
                     * Check for duplicate e-mail error
                     */
                    if (e.errorCode === 400009 && e.validationErrors[0].errorCode === 400003) {
                        const field = form.querySelector("#sign-up-email");

                        field.dataset.validateCheck = Validate.validateField(field, () => false, true, window.snakeCheckout.translations["error.field.email.duplicate"].replace("%s", import.meta.env.CO_BASE_DOMAIN + "/" + window.snakeCheckout.currentCountry + "/myarea/p/reset"));
                    } else {
                        Utils.modal("popup", true, this.modalI18nGenericOptions);

                        AnalyticsService.errorEvent("1_registration", "technical", "gproxy | sign-up error", e);
                    }
                });

            Utils.spinner("loader", false);
        });

        if (!Identity.isAgent() && !Identity.isAgentCustomer()) {
            const signInForm = componentDom.querySelector(".sign-in > form");
            Validate.attach(signInForm, (formIsValid) => (signInForm.querySelector('button[type="submit"]').disabled = !formIsValid));

            /**
             * Listener to intercept the Sign In user action
             */
            Hook.subscribe("_signIn", async (e) => {
                Trace.warn("_signIn");

                Utils.spinner("loader", true);

                const formData = new FormData(e.detail.event.target.parentElement.closest("form"));

                await Identity.signIn(formData.get("sign-in-email"), formData.get("sign-in-password"), formData.get("sign-in-remember-me"))
                    .then(async (response) => {
                        Trace.warn("_signIn Identity.signIn");
                        Trace.log(response);

                        if (response.errorCode !== 0) {
                            Utils.modal("popup", true, { text: window.snakeCheckout.translations["error.signin"], description: window.snakeCheckout.translations["discovery.mixedUserAltText"], dismissBtn: this.modalI18nGenericOptions.dismissBtn });

                            AnalyticsService.errorEvent("1_login", "technical", "gproxy | sign-in response error", response);
                        } else {
                            Trace.warn("_signUp Identity.signUp PRE LOAD");

                            Component.load(config.routing.sections.load.component);

                            Trace.warn("_signUp Identity.signUp POST LOAD");
                        }
                    })
                    .catch((e) => {
                        if (e.errorCode === 403042) Utils.modal("popup", true, { text: window.snakeCheckout.translations["error.signin"], description: window.snakeCheckout.translations["discovery.mixedUserAltText"], dismissBtn: this.modalI18nGenericOptions.dismissBtn });
                        else {
                            Utils.modal("popup", true, this.modalI18nGenericOptions);

                            AnalyticsService.errorEvent("1_login", "technical", "gproxy | sign-in error", e);
                        }
                    });

                Utils.spinner("loader", false);
            });
        }

        componentDom.querySelector(".not-auth").classList.remove("hidden");
        componentDom.querySelector(".auth").classList.add("hidden");
    };

    static #renderUserProfile = () => {
        Trace.warn("renderUserProfile");

        const componentDom = document.getElementById(config.routing.sections.profile.component);

        /**
         * Replace HTML placeholders with user data
         */
        componentDom.querySelectorAll(".auth firstname").forEach((element) => {
            element.innerHTML = window.snakeCheckout.accountInfo.profile.firstName;
        });
        componentDom.querySelector(".auth lastname").innerHTML = window.snakeCheckout.accountInfo.profile.lastName;
        componentDom.querySelector(".auth email").innerHTML = window.snakeCheckout.accountInfo.profile.email;

        componentDom.querySelector(".auth .profile-info").classList.remove("hidden");
        componentDom.querySelector(".auth form").classList.add("hidden");
        componentDom.querySelector(".auth").classList.remove("hidden");
        componentDom.querySelector(".not-auth").classList.add("hidden");
    };
}
